import React from 'react';

import {Collaborator} from 'auth/PermissionConstants';
import {convertCollaborationsToCollaborators} from 'auth/PermissionConstants';
import {Collaboration} from 'collaborator-components/useCollaborationManagementState';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  OwnableSettingsModalProps,
  OwnableSettingsWithCollaborationsModal,
} from 'create-components/OwnableSettingsModal';
import {toServerCollaborators} from 'create-components/OwnableUtil';
import {getStore} from 'redux-stores/Store';
import {asJQueryPromise} from 'util/Promises';
import {createWorklistAsync, RefreshOptions} from 'worklists/WorklistActions';
import {
  WorklistType,
  worklistTypeDisplayName,
  WorklistWithOwnerAndCollaborators,
  WORKLIST_DISABLED_OWNER_REASON,
} from 'worklists/WorklistConstants';

export interface CreateWorklistWithCollaboratorsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  refreshOptions?: RefreshOptions;
  worklistType: WorklistType;
}

/**
 * Modal component to create a new worklist when we do not have any items,
 * and the user has to select a type. Allows user to select collaborators.
 */
export default function CreateWorklistWithCollaboratorsModal(
  props: CreateWorklistWithCollaboratorsModalProps
): JSX.Element {
  function createWorklistWithCollaborators(data: {
    name: string;
    collaborators?: Array<Collaborator>;
    collaborations?: Array<Collaboration>;
  }): JQueryPromise<WorklistWithOwnerAndCollaborators> {
    const clientCollaborators =
      data.collaborators ??
      convertCollaborationsToCollaborators((data.collaborations ?? []).filter((c) => !c.fromOwnership));
    const serverCollaborators = toServerCollaborators(clientCollaborators);
    return asJQueryPromise(
      createWorklistAsync(
        data.name,
        props.worklistType,
        [],
        props.refreshOptions,
        serverCollaborators,
        getStore().dispatch.bind(getStore())
      )
    );
  }

  const ownableSettingsProps: OwnableSettingsModalProps = {
    title: `Create new ${worklistTypeDisplayName(props.worklistType)} worklist`,
    ownableType: ModelTypeConstants.WORKLIST,
    onRequestClose: props.onRequestClose,
    navigateOnSuccess: false,
    doSubmit: createWorklistWithCollaborators,
    enableDescription: false,
    requiresCreatorOwnership: true,
    disableChangeOwner: true,
    disabledOwnerTooltipText: WORKLIST_DISABLED_OWNER_REASON,
    initialCollaborators: [],
  };

  return <OwnableSettingsWithCollaborationsModal {...ownableSettingsProps} />;
}
